import type { DrawingEntity, Point } from '../contract/types.js';

/**
 * Point and path distances, shared by matching and by fragmentation counting.
 *
 * The distinction that matters here: the Hausdorff distance between two
 * polylines is measured **from each point to the other path**, not to the other
 * path's vertices. Measuring vertex to vertex is a different quantity and it is
 * wrong in a way that bites constantly — a producer emitting a redundant
 * collinear vertex describes exactly the same shape, but a vertex-to-vertex
 * comparison sees that extra point as half a segment away from anything.
 */

export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export function distanceToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return distance(p, a);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSquared;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** Distance from a point to the nearest point anywhere on a polyline. */
export function distanceToPolyline(p: Point, path: readonly Point[], closed = false): number {
  if (path.length === 0) return Infinity;
  if (path.length === 1) return distance(p, path[0]!);

  let best = Infinity;
  for (let i = 1; i < path.length; i++) {
    best = Math.min(best, distanceToSegment(p, path[i - 1]!, path[i]!));
  }
  if (closed) {
    best = Math.min(best, distanceToSegment(p, path[path.length - 1]!, path[0]!));
  }
  return best;
}

export function directedHausdorff(
  from: readonly Point[],
  to: readonly Point[],
  toClosed = false,
): number {
  let worst = 0;
  for (const p of from) worst = Math.max(worst, distanceToPolyline(p, to, toClosed));
  return worst;
}

export function symmetricHausdorff(
  a: readonly Point[],
  b: readonly Point[],
  closed = false,
): number {
  if (a.length === 0 || b.length === 0) return Infinity;
  return Math.max(directedHausdorff(a, b, closed), directedHausdorff(b, a, closed));
}

/**
 * A sampled representation of an entity's geometry.
 *
 * Curves are sampled rather than described, because the question these points
 * answer — does this shape lie along that one — has no closed form worth
 * writing for a mixture of lines, arcs and polylines.
 */
export function sampledPath(e: DrawingEntity): Point[] {
  switch (e.type) {
    case 'LINE':
      return [e.start, { x: (e.start.x + e.end.x) / 2, y: (e.start.y + e.end.y) / 2 }, e.end];
    case 'LWPOLYLINE':
      return sampledPolyline(e.vertices, e.bulges, e.closed);
    case 'CIRCLE':
      return sampleArc(e.centre, e.radius, 0, 360, 16);
    case 'ARC':
      return sampleArc(e.centre, e.radius, e.startAngle, e.endAngle, 8);
    case 'TEXT':
    case 'MTEXT':
      return [e.anchor];
  }
}

/** Every sampled point of `candidate` lies within tolerance of `path`. */
export function liesOnPath(
  candidate: readonly Point[],
  path: readonly Point[],
  tolerance: number,
): boolean {
  if (candidate.length === 0) return false;
  return candidate.every((q) => distanceToPolyline(q, path) <= tolerance);
}

/**
 * Bulge arithmetic, so a curved polyline segment is sampled as the curve it is.
 *
 * A DXF bulge is `tan(θ/4)` for the segment's included angle, signed positive
 * for a counter-clockwise sweep. It describes the arc **exactly** — which is why
 * answer 16 sanctions it and why flattening the corners into extra vertices was
 * refused: that would be the fitting this design has always declined.
 *
 * Sampling the vertices alone means a rounded rectangle and a chamfered one are
 * the same list of points, so every distance in this module returned zero
 * between them.
 */
export const BULGE_ARC_STEPS = 8;

export function sampledPolyline(
  vertices: readonly Point[],
  bulges: readonly number[],
  closed: boolean,
): Point[] {
  const out: Point[] = [];
  const n = vertices.length;
  for (let i = 0; i < n; i++) {
    const from = vertices[i]!;
    out.push(from);
    const last = i === n - 1;
    if (last && !closed) break;
    const to = vertices[last ? 0 : i + 1]!;
    const bulge = bulges[i] ?? 0;
    if (bulge !== 0) out.push(...bulgeInterior(from, to, bulge));
  }
  return out;
}

/**
 * The interior points of the arc a bulge describes, endpoints excluded.
 *
 * Signed throughout: the centre offset and the radius both come out negative for
 * a clockwise sweep, and the sampling walks the signed angle, so a corner rounded
 * the other way samples on the other side of the chord rather than in the same
 * place.
 */
function bulgeInterior(from: Point, to: Point, bulge: number): Point[] {
  const theta = 4 * Math.atan(bulge);
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const chord = Math.hypot(dx, dy);
  if (chord === 0 || !Number.isFinite(theta) || theta === 0) return [];

  // Distance from the chord's midpoint to the centre, along the LEFT
  // perpendicular of from->to. Signed: negative for a clockwise sweep, which
  // puts the centre on the other side.
  const offset = chord / 2 / Math.tan(theta / 2);
  const cx = (from.x + to.x) / 2 + (-dy / chord) * offset;
  const cy = (from.y + to.y) / 2 + (dx / chord) * offset;
  const radius = Math.hypot(from.x - cx, from.y - cy);
  if (!(radius > 0) || !Number.isFinite(radius)) return [];

  const a0 = Math.atan2(from.y - cy, from.x - cx);
  const out: Point[] = [];
  for (let i = 1; i < BULGE_ARC_STEPS; i++) {
    const a = a0 + (theta * i) / BULGE_ARC_STEPS;
    out.push({ x: cx + radius * Math.cos(a), y: cy + radius * Math.sin(a) });
  }
  return out;
}

/**
 * How far a bulge difference moves the arc from where it should be.
 *
 * The apex of the arc sits `bulge x chord / 2` from the chord's midpoint, so a
 * difference of `Δb` displaces it by `Δb x chord / 2`. Expressing the
 * disagreement as a **distance** is what keeps the bulge comparison on the
 * board's tolerance rather than needing a second, dimensionless one — and it is
 * geometrically right: the same bulge error on a 1 mm corner and on a 100 mm one
 * are not the same error.
 */
export function bulgeDisplacement(
  from: Point,
  to: Point,
  a: number,
  b: number,
): number {
  const chord = Math.hypot(to.x - from.x, to.y - from.y);
  return (Math.abs(a - b) * chord) / 2;
}

function sampleArc(
  centre: Point,
  radius: number,
  startDeg: number,
  endDeg: number,
  steps: number,
): Point[] {
  const sweep = (((endDeg - startDeg) % 360) + 360) % 360 || 360;
  const out: Point[] = [];
  for (let i = 0; i <= steps; i++) {
    const a = ((startDeg + (sweep * i) / steps) * Math.PI) / 180;
    out.push({ x: centre.x + radius * Math.cos(a), y: centre.y + radius * Math.sin(a) });
  }
  return out;
}
